import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { AnalyticsDashboard } from '@/components/admin/analytics-dashboard';
import { SystemMonitor } from '@/components/admin/system-monitor';
import { CommentModeration } from '@/components/admin/comment-moderation';
import { UserActivityTimeline } from '@/components/admin/user-activity-timeline';
import { ContentAnalytics } from '@/components/admin/content-analytics';
import { WebhookLogs } from '@/components/admin/webhook-logs';
import FilterBar from '@/components/common/filter-bar';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/stores/auth';
import { AdminLayout } from '@/components/layouts/admin-layout';



// Admin dashboard stats interface
interface AdminStats {
  users: {
    total: number;
    active: number;
    newToday: number;
    pending: number;
    newThisWeek: number;
  };
  streams: {
    total: number;
    live: number;
    scheduled: number;
    averageViewers: number;
  };
  comments: {
    total: number;
    pendingModeration: number;
    moderated: number;
  };
  system: {
    uptime: string;
    apiRequests: number;
    errorRate: number;
    storageUsed: string;
  };
}

export default function AdminDashboardPage() {
  const { user } = useAuthStore();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchParams] = useSearchParams();

  // Filter state for system tab
  const [systemFilters, setSystemFilters] = useState<Record<string, string | string[]>>(() => {
    const initialFilters: Record<string, string | string[]> = {};
    
    // Read filters from URL
    const component = searchParams.get('systemComponent');
    const status = searchParams.get('systemStatus');
    const search = searchParams.get('systemSearch');
    
    if (search) initialFilters.search = search;
    if (component && component !== 'all') initialFilters.component = component;
    if (status && status !== 'all') initialFilters.status = status;
    
    return initialFilters;
  });

  // Filter state for analytics tab
  const [analyticsFilters, setAnalyticsFilters] = useState<Record<string, string | string[]>>(() => {
    const initialFilters: Record<string, string | string[]> = {};
    
    // Read filters from URL
    const metricType = searchParams.get('analyticsMetricType');
    const userRole = searchParams.get('analyticsUserRole');
    const dateRange = searchParams.get('analyticsDateRange');
    
    if (metricType && metricType !== 'all') initialFilters.metricType = metricType;
    if (userRole && userRole !== 'all') initialFilters.userRole = userRole;
    if (dateRange) {
      const dates = dateRange.split(',');
      if (dates.length >= 1) {
        initialFilters.dateRange = dates.length === 2 ? dates : [dates[0], ''];
      }
    }
    
    return initialFilters;
  });

  useEffect(() => {
    fetchAdminStats();
  }, []);

  const fetchAdminStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get<{ stats: AdminStats }>('/api/admin/stats');

      if (response.success && response.data) {
        setStats(response.data.stats);
      } else {
        setError(response.error?.message || 'Failed to load admin statistics');
      }
    } catch (err) {
      console.error('Error fetching admin stats:', err);
      setError('Failed to load admin statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <AdminLayout>
      {/* Quick Stats */}
      {stats && (
        <div
          className="grid gap-2 @[760px]/admin-layout:gap-3 @[1200px]/admin-layout:gap-4"
          style={{
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))'
          }}
        >
          <Card>
            <CardContent className="p-3 @[760px]/admin-layout:p-4">
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Total Users</p>
                <p className="text-2xl @[760px]/admin-layout:text-3xl font-bold">{stats.users.total.toLocaleString()}</p>
                <p className="text-xs text-green-600">+{stats.users.newToday} today</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3 @[760px]/admin-layout:p-4">
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Live Streams</p>
                <p className="text-2xl @[760px]/admin-layout:text-3xl font-bold">{stats.streams.live}</p>
                <p className="text-xs text-muted-foreground">{stats.streams.total} total</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3 @[760px]/admin-layout:p-4">
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Pending Comments</p>
                <p className="text-2xl @[760px]/admin-layout:text-3xl font-bold">{stats.comments.pendingModeration}</p>
                <p className="text-xs text-muted-foreground">{stats.comments.total} total</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3 @[760px]/admin-layout:p-4">
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">System Health</p>
                <p className="text-2xl @[760px]/admin-layout:text-3xl font-bold">{Math.round((1 - stats.system.errorRate) * 100)}%</p>
                <p className="text-xs text-muted-foreground">{stats.system.uptime}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 @[1120px]/admin-layout:space-y-6">
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 @[760px]/admin-layout:grid-cols-3 @[1120px]/admin-layout:grid-cols-5">
          <TabsTrigger className="text-xs @[760px]/admin-layout:text-sm" value="overview">Overview</TabsTrigger>
          <TabsTrigger className="text-xs @[760px]/admin-layout:text-sm" value="analytics">Analytics</TabsTrigger>
          <TabsTrigger className="text-xs @[760px]/admin-layout:text-sm" value="moderation">Moderation</TabsTrigger>
          <TabsTrigger className="text-xs @[760px]/admin-layout:text-sm" value="system">System</TabsTrigger>
          <TabsTrigger className="text-xs @[760px]/admin-layout:text-sm" value="webhooks">Webhooks</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 @[1120px]/admin-layout:space-y-6">
          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest system events and user actions</CardDescription>
            </CardHeader>
            <CardContent>
              <UserActivityTimeline userId={user?.id || 0} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics">
          <div className="space-y-4 @[1120px]/admin-layout:space-y-6">
            {/* Filters */}
            <div className="p-2 sm:p-3 @[1120px]/admin-layout:p-4">
              <FilterBar
                filters={[
                  {
                    id: 'analyticsDateRange',
                    label: 'Date Range',
                    type: 'date-range'
                  },
                  {
                    id: 'analyticsMetricType',
                    label: 'Metric Type',
                    type: 'select',
                    options: [
                      { value: 'all', label: 'All Metrics' },
                      { value: 'views', label: 'Views' },
                      { value: 'engagement', label: 'Engagement' },
                      { value: 'streams', label: 'Streams' },
                      { value: 'users', label: 'Users' },
                      { value: 'sessions', label: 'Sessions' },
                      { value: 'bounce_rate', label: 'Bounce Rate' }
                    ],
                    placeholder: 'All Metrics'
                  },
                  {
                    id: 'analyticsUserRole',
                    label: 'User Role',
                    type: 'multiselect',
                    options: [
                      { value: 'member', label: 'Member' },
                      { value: 'pastor', label: 'Pastor' },
                      { value: 'media_team', label: 'Media Team' },
                      { value: 'moderator', label: 'Moderator' },
                      { value: 'admin', label: 'Admin' }
                    ],
                    placeholder: 'All Roles'
                  }
                ]}
                activeFilters={analyticsFilters}
                onFilterChange={setAnalyticsFilters}
              />
            </div>

            <AnalyticsDashboard />
          </div>
        </TabsContent>

        <TabsContent value="moderation" className="space-y-4 @[1120px]/admin-layout:space-y-6">
          <div className="grid grid-cols-1 gap-4 @[1120px]/admin-layout:grid-cols-2 @[1120px]/admin-layout:gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Comment Moderation</CardTitle>
                <CardDescription>Manage user comments and content</CardDescription>
              </CardHeader>
              <CardContent>
                <CommentModeration />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Content Analytics</CardTitle>
                <CardDescription>Analyze content engagement and performance</CardDescription>
              </CardHeader>
              <CardContent>
                <ContentAnalytics />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="system">
          <div className="space-y-4 @[1120px]/admin-layout:space-y-6">
            {/* Filters */}
            <div className="p-2 sm:p-3 @[1120px]/admin-layout:p-4">
              <FilterBar
                filters={[
                  {
                    id: 'systemSearch',
                    label: 'Search',
                    type: 'search',
                    placeholder: 'Search system events...'
                  },
                  {
                    id: 'systemComponent',
                    label: 'Component',
                    type: 'select',
                    options: [
                      { value: 'all', label: 'All Components' },
                      { value: 'api', label: 'API' },
                      { value: 'database', label: 'Database' },
                      { value: 'cache', label: 'Cache' },
                      { value: 'auth', label: 'Auth' },
                      { value: 'storage', label: 'Storage' },
                      { value: 'worker', label: 'Worker' },
                      { value: 'cdn', label: 'CDN' },
                      { value: 'stream', label: 'Stream' },
                      { value: 'notification', label: 'Notification' }
                    ],
                    placeholder: 'All Components'
                  },
                  {
                    id: 'systemStatus',
                    label: 'Status',
                    type: 'select',
                    options: [
                      { value: 'all', label: 'All Status' },
                      { value: 'healthy', label: 'Healthy' },
                      { value: 'warning', label: 'Warning' },
                      { value: 'critical', label: 'Critical' },
                      { value: 'degraded', label: 'Degraded' },
                      { value: 'offline', label: 'Offline' }
                    ],
                    placeholder: 'All Status'
                  }
                ]}
                activeFilters={systemFilters}
                onFilterChange={setSystemFilters}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 @[1120px]/admin-layout:grid-cols-2 @[1120px]/admin-layout:gap-6">
              <Card>
                <CardContent className="pt-6">
                  <SystemMonitor />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Database Status</CardTitle>
                  <CardDescription>Database health and performance metrics</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium">Status</span>
                    <Badge variant="default">Healthy</Badge>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium">API Requests (24h)</span>
                    <span className="text-sm text-muted-foreground">
                      {stats?.system.apiRequests?.toLocaleString() ?? '—'}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium">Error Rate</span>
                    <span className="text-sm text-muted-foreground">
                      {((stats?.system.errorRate ?? 0) * 100).toFixed(2)}%
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium">Uptime</span>
                    <span className="text-sm text-muted-foreground">
                      {stats?.system.uptime}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="webhooks">
          <Card>
            <CardHeader>
              <CardTitle>Webhook Logs</CardTitle>
              <CardDescription>Monitor webhook deliveries and failures</CardDescription>
            </CardHeader>
            <CardContent>
              <WebhookLogs />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {error && (
        <Card className="border-destructive">
          <CardContent className="p-4">
            <p className="text-destructive text-center">{error}</p>
            <Button
              variant="outline"
              onClick={fetchAdminStats}
              className="mt-2 w-full"
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
}
